import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import ormStationsService from "../../hooks/api/stations";
import apiService from "../../hooks/api/axios";

export interface DateRange {
  start: string | null;
  end: string | null;
}

export interface TimeRange {
  start: string | null;
  end: string | null;
}

export interface Sid {
  token?: string;
  expire?: string;
}

export interface UserSessionState {
  reservationDate: DateRange;
  reservationTime: TimeRange;
  selectedStation: string | null;
  selectedStationEnd: string | null;
  stationtype: string | null;
  carcategoryselect: any | null;
  stationBranch: string | null;
  startDate: string | null;
  endDate: string | null;
  startTime: string | null;
  endTime: string | null;
  cargroup: string | null;
  prices: any;
  pricesLoading: Record<string, boolean>;
  tariffs: any;
  tariffsLoading: Record<string, any>;
  sid: any;
  rate_code: string;
  offer: string;
}
const initialState: UserSessionState = {
  reservationDate: { start: "", end: "" },
  reservationTime: { start: "", end: "" },
  selectedStation: "",
  selectedStationEnd: null,
  stationtype: "RENT",
  carcategoryselect: null,
  stationBranch: "RENT",
  startDate: "",
  endDate: "",
  startTime: "",
  endTime: "",
  cargroup: null,
  prices: {},
  pricesLoading: {},
  tariffs: {},
  tariffsLoading: {},
  sid: {},
  rate_code: "",
  offer: "",
};

const loadSessionFromStorage = (): UserSessionState => {
  try {
    const key = `ORM${process.env.NODE_ENV === "development" ? "_" + process.env.REACT_APP_DEPLOYCUSTOMERID : ""}_userSession`;
    const stored = sessionStorage.getItem(key);

    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        ...initialState,
        ...parsed,
        prices: parsed.prices || {},
        pricesLoading: parsed.pricesLoading || {},
        tariffs: parsed.tariffs || {},
        tariffsLoading: parsed.tariffsLoading || {},
      };
    }
  } catch (error) {
    console.error("❌ Error loading session from storage:", error);
  }

  console.log("🆕 Using initial state");
  return initialState;
};

const userSessionSlice = createSlice({
  name: "userSession",
  initialState: loadSessionFromStorage(),
  reducers: {
    setReservationDate: (state, action: PayloadAction<DateRange>) => {
      state.reservationDate = action.payload;
    },
    setReservationTime: (state, action: PayloadAction<TimeRange>) => {
      state.reservationTime = action.payload;
    },
    setInitStation: (state, action: PayloadAction<any>) => {
      if (state.selectedStation !== action.payload.stationId) {
        state.prices = {};
        state.pricesLoading = {};
      }
      if (action.payload.type === "start") {
        state.selectedStation = action.payload.stationId;
      } else {
        state.selectedStationEnd = action.payload.stationId;
      }
    },
    setSelectedStation: (state, action: PayloadAction<any>) => {
      if (state.selectedStation !== action.payload.stationId) {
        state.prices = {};
        state.pricesLoading = {};
      }
      if (action.payload.type === "start") {
        state.selectedStation = action.payload.stationId;
      } else {
        state.selectedStationEnd = action.payload.stationId;
      }

      ormStationsService.fetchOpeningTimes(state);
    },
    setStationType: (state, action: PayloadAction<string | null>) => {
      state.stationtype = action.payload;
    },
    setCarCategorySelect: (state, action: PayloadAction<any>) => {
      state.carcategoryselect = action.payload;
    },
    setStationBranch: (state, action: PayloadAction<string | null>) => {
      state.stationBranch = action.payload;
    },
    // Generische Setter-Funktion (wie dein setUserSession)
    setUserSessionField: (
      state,
      action: PayloadAction<{ key: keyof UserSessionState; value: any }>,
    ) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    setStartDate: (state, action: PayloadAction<any>) => {
      state.startDate = action.payload;
      state.reservationDate!.start = action.payload;

      // ormStationsService.fetchOpeningTimes(state);
    },
    setEndDate: (state, action: PayloadAction<any>) => {
      state.endDate = action.payload;
      state.reservationDate!.end = action.payload;
      // ormStationsService.fetchOpeningTimes(state);
    },
    setStartTime: (state, action: PayloadAction<any>) => {
      state.startTime = action.payload;
      state.reservationTime!.start = action.payload;
      // ormStationsService.fetchOpeningTimes(state);
    },
    setEndTime: (state, action: PayloadAction<any>) => {
      state.endTime = action.payload;
      state.reservationTime!.end = action.payload;
      // ormStationsService.fetchOpeningTimes(state);
    },
    setSelectedVehicle: (
      state,
      action: PayloadAction<{
        vehicleId: string | null;
        categories: string[] | null;
      }>,
    ) => {
      if (!action.payload.categories) {
        sessionStorage.removeItem(
          `ORM${process.env.NODE_ENV === "development" ? "_" + process.env.REACT_APP_DEPLOYCUSTOMERID : ""}_selectedCarCategory`,
        );
        return;
      }
      state.cargroup = action.payload.vehicleId;
      try {
        const categoryObject = action.payload.categories.reduce(
          (acc, category) => {
            acc[category] = true;
            return acc;
          },
          {} as Record<string, boolean>,
        );

        sessionStorage.setItem(
          `ORM${process.env.NODE_ENV === "development" ? "_" + process.env.REACT_APP_DEPLOYCUSTOMERID : ""}_selectedCarCategory`,
          JSON.stringify(categoryObject),
        );
      } catch (error) {
        console.error("Error storing selected car category:", error);
      }
    },
    setSid: (state) => {
      const loginsession = localStorage.getItem(
        `ORM${process.env.NODE_ENV === "development" ? "_" + process.env.REACT_APP_DEPLOYCUSTOMERID : ""}_LoginSession`,
      ) as string;
      state.sid = JSON.parse(loginsession) || {};
      apiService.setToken(state.sid);
    },
    setOfferSelected: (state, action: PayloadAction<any>) => {
      if (action.payload == null) {
        sessionStorage.removeItem(
          `ORM${process.env.NODE_ENV === "development" ? "_" + process.env.REACT_APP_DEPLOYCUSTOMERID : ""}_offer_selected`,
        );
        return;
      }

      sessionStorage.setItem(
        `ORM${process.env.NODE_ENV === "development" ? "_" + process.env.REACT_APP_DEPLOYCUSTOMERID : ""}_offer_selected`,
        JSON.stringify(action.payload),
      );
      state.offer = action.payload;
      state.rate_code = action.payload!.rate_code!;
    },
    resetUserSession: () => initialState,
    setPriceLoading: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.pricesLoading[id] = true;
    },
    setPriceSuccess: (
      state,
      action: PayloadAction<{ id: string; data: any }>,
    ) => {
      const { id, data } = action.payload;
      const finalPrice = data?.total_price_main_rates || "";
      const finalUnit = data?.main_rates?.[0]?.unit || "";
      // Initialize prices[id] if not exists
      if (!state.prices[id]) {
        state.prices[id] = {};
      }
      state.prices[id] = {
        price: finalPrice,
        unit: finalUnit,
      };

      state.pricesLoading[id] = false;
    },
    setPriceError: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.pricesLoading[id] = false;
      state.prices[id] = null;
    },
    setTariffsLoading: (
      state,
      action: PayloadAction<{ id: string; type: string }>,
    ) => {
      const { id, type } = action.payload;

      // Initialize if not exists
      if (!state.tariffsLoading[id]) {
        state.tariffsLoading[id] = {};
      }

      // Set only this specific type, preserve others
      state.tariffsLoading[id][type] = true;
    },

    setTariffsSuccess: (
      state,
      action: PayloadAction<{ id: string; type: string; data: any }>,
    ) => {
      const { id, type, data } = action.payload;

      // Extract the values
      const finalPrice = data?.total_price_main_rates || "";
      const finalUnit = data?.main_rates?.[0]?.unit || "";

      // Initialize tariffs[id] if not exists
      if (!state.tariffs[id]) {
        state.tariffs[id] = {};
      }

      // Store the price data
      state.tariffs[id][type] = {
        price: finalPrice,
        unit: finalUnit,
      };

      // Initialize tariffsLoading[id] if not exists
      if (!state.tariffsLoading[id]) {
        state.tariffsLoading[id] = {};
      }

      // Set loading to false for this specific type
      state.tariffsLoading[id][type] = false;
    },

    setTariffsError: (
      state,
      action: PayloadAction<{ id: string; type: string }>,
    ) => {
      const { id, type } = action.payload;

      // Initialize if not exists
      if (!state.tariffsLoading[id]) {
        state.tariffsLoading[id] = {};
      }

      // Set loading to false
      state.tariffsLoading[id][type] = false;

      // Optional: Mark error state in tariffs
      if (!state.tariffs[id]) {
        state.tariffs[id] = {};
      }
      state.tariffs[id][type] = null;
    },
  },
});

export const {
  setReservationDate,
  setReservationTime,
  setSelectedStation,
  setInitStation,
  setStationType,
  setCarCategorySelect,
  setStationBranch,
  setUserSessionField,
  setStartDate,
  setEndDate,
  setStartTime,
  setEndTime,
  setSelectedVehicle,
  resetUserSession,
  setPriceLoading,
  setPriceSuccess,
  setPriceError,
  setTariffsLoading,
  setTariffsSuccess,
  setTariffsError,
  setSid,
  setOfferSelected,
} = userSessionSlice.actions;

export default userSessionSlice.reducer;
